import GenerateDialogues from 'features/podcast/modals/GenerateDialogues';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addCharacter, removeCharacter } from 'store/slices/podcastGen';
import HostForm, { CharacterErrors, hostSchema } from './HostForm';
import GuestForm, { guestSchema } from './GuestForm';
import { RootState } from 'store';
import { ArrowLeft, X } from 'lucide-react';
import { z } from 'zod';

interface PodCastFormAssignVoicesProps {
  onAssignVoice: () => void;
}

const PodCastFormAssignVoices = ({
  onAssignVoice,
}: PodCastFormAssignVoicesProps) => {
  const data = ['Voice', 'Name', 'Age', 'Qualities'];
  const dispatch = useDispatch();
  const { hosts, guests } = useSelector((state: RootState) => state.podcastGen);

  const [hostsNumber, setHostsNumber] = useState<number>(1);
  const [guestsNumber, setGuestsNumber] = useState<number>(1);
  
  // Maintain error states for hosts and guests
  const [hostErrors, setHostErrors] = useState<Record<number, CharacterErrors>>({});
  const [guestErrors, setGuestErrors] = useState<Record<number, CharacterErrors>>({});

  // Function to validate a field for a host
  const validateField = (
    index: number,
    field: 'character_name' | 'age' | 'voice',
    value: any,
    role: 'host' | 'guest'
  ) => {
    try {
      const schema = role === 'host' ? hostSchema : guestSchema;
      const character = role === 'host' ? hosts[index] : guests[index];
      
      schema.parse({ ...character, [field]: value });
      
      if (role === 'host') {
        setHostErrors((prev) => ({
          ...prev,
          [index]: { ...(prev[index] || {}), [field]: undefined }
        }));
      } else {
        setGuestErrors((prev) => ({
          ...prev,
          [index]: { ...(prev[index] || {}), [field]: undefined }
        }));
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldError = error.errors.find((err) => err.path[0] === field);
        if (role === 'host') {
          setHostErrors((prev) => ({
            ...prev,
            [index]: { 
              ...(prev[index] || {}), 
              [field]: fieldError?.message 
            }
          }));
        } else {
          setGuestErrors((prev) => ({
            ...prev,
            [index]: { 
              ...(prev[index] || {}), 
              [field]: fieldError?.message 
            }
          }));
        }
      }
    }
  };

  // Function to set errors for a specific host
  const setHostErrorsForIndex = (index: number, errors: CharacterErrors) => {
    setHostErrors((prev) => ({
      ...prev,
      [index]: { ...(prev[index] || {}), ...errors }
    }));
  };

  // Function to set errors for a specific guest
  const setGuestErrorsForIndex = (index: number, errors: CharacterErrors) => {
    setGuestErrors((prev) => ({
      ...prev,
      [index]: { ...(prev[index] || {}), ...errors }
    }));
  };

  // to add host and guest in global state with increase in number
  const handleAddHosts = () => {
    if (hostsNumber < 5) {
      setHostsNumber((prev) => prev + 1);
      dispatch(
        addCharacter({
          character_name: '',
          role: 'host',
          gender: 'M', // Default gender
          age: 0, // Default age
        })
      );
    }
  };

  const handleAddGuests = () => {
    if (guestsNumber < 3) {
      setGuestsNumber((prev) => prev + 1);
      dispatch(
        addCharacter({
          character_name: '',
          role: 'guest',
          gender: 'F',
          age: 0,
        })
      );
    }
  };

  const handleRemove = (role: 'host' | 'guest', index: number) => {
    if (role === 'guest') {
      setGuestsNumber((prev) => prev - 1);
      // Remove errors for this guest
      setGuestErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
    } else {
      setHostsNumber((prev) => prev - 1);
      // Remove errors for this host
      setHostErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
    }
    dispatch(removeCharacter({ role, index }));
  };

  const isNameDuplicate = (
    name: string,
    index: number,
    role: 'host' | 'guest',
    field: 'character_name' | 'voice_name' = 'character_name'
  ) => {
    // Combine both hosts and guests
    const allCharacters = [...hosts, ...guests];
    return allCharacters.some((character, i) => {
      // Exclude the current character based on its role and index
      if (
        (role === 'host' && character.role === 'host' && i === index) ||
        (role === 'guest' && character.role === 'guest' && i === index)
      ) {
        return false;
      }
      // Check if the name matches based on the field type
      return character[field] === name;
    });
  };

  return (
    <div className="w-full">
      <button type="button" onClick={onAssignVoice}>
        <ArrowLeft className="sm:hidden mb-4 flex" color="#ffffff" size={18} />
      </button>
      <div className=" text-text-primary flex flex-col sm:flex-row sm:h-[100px] sm:max-h-[100px] items-start sm:items-center gap-4 sm:gap-0 mb-6 sm:mb-0">
        <div className="flex justify-between w-[340px] sm:justify-start sm:w-full">
          <div className="sm:mr-[50px]">
            <div className="text-xs sm:text-sm text-text-secondary">STEP 1</div>
            <div className="text-lg sm:text-[25px] font-normal text-text-secondary">
              Enter a Topic
            </div>
          </div>
          <div className="">
            <div className="text-xs sm:text-sm text-text-primary">STEP 2</div>
            <div className="text-lg sm:text-[25px] font-bold text-text-primary">
              Assign Characters
            </div>
          </div>
        </div>
        <div className="hidden sm:flex sm:ml-auto mt-4 sm:mt-0">
          <GenerateDialogues hostErrors={hostErrors} guestErrors={guestErrors} />
        </div>
      </div>
      <div className="bg-background text-text-primary p-4 sm:p-10 rounded-lg">
        <div className="flex flex-wrap items-center py-[10px] border-b-2">
          <div className="text-xl sm:text-[25px] font-bold">Hosts*</div>
          <button
            type="button"
            className="ml-auto text-sm sm:text-base text-primary underline underline-offset-4 disabled:text-[#000F08]/30"
            onClick={handleAddHosts}
            disabled={hostsNumber >= 5}
          >
            + Add host (upto 5)
          </button>
        </div>
        <div className="mt-0 sm:mt-4">
          <div className="hidden sm:grid sm:grid-cols-4 sm:gap-2">
            {data.map((item, index) => (
              <div
                key={`host-title-${index}`}
                className="text-[14px]  font-medium"
              >
                {item}
              </div>
            ))}
          </div>
          <div className="gap-y-4">
            {Array(hostsNumber)
              .fill(1)
              .map((_, index) => (
                <div
                  className={`relative mt-5 ${index !== 0 ? 'border-t-2 sm:border-t-0' : 'border-t-0'}`}
                  key={`host-${index}`}
                >
                  <div className="flex justify-between mt-[16px] mb-3 sm:hidden sm:mb-0">
                    <span className="text-primary font-normal text-[12px]">
                      Host-{index + 1}
                    </span>
                    <X
                      size={16}
                      color="#5F6563"
                      onClick={() => handleRemove('host', index)}
                    />
                  </div>
                  <HostForm 
                    index={index} 
                    isDuplicate={isNameDuplicate} 
                    errors={hostErrors[index] || {}}
                    setErrors={setHostErrorsForIndex}
                    validateField={validateField}
                  />
                  <X
                    className={`sm:absolute sm:-right-7 sm:top-2 sm:cursor-pointer ${
                      index === 0 ? 'hidden' : 'hidden sm:block'
                    }`}
                    color="#846CEC"
                    size={20}
                    onClick={() => handleRemove('host', index)}
                  />
                </div>
              ))}
          </div>
        </div>
      </div>
      <div className="bg-background p-4 text-text-primary sm:p-10 mt-[15px] rounded-lg">
        <div className="flex flex-wrap items-center py-[10px] border-b-2">
          <div className="text-xl sm:text-[25px] font-bold">Guests*</div>
          <button
            type="button"
            className="ml-auto text-sm sm:text-base text-primary underline underline-offset-4 disabled:text-primary/60"
            onClick={handleAddGuests}
            disabled={guestsNumber >= 3}
          >
            + Add Guests (upto 3)
          </button>
        </div>
        <div className="mt-0 sm:mt-4">
          <div className="hidden sm:grid sm:grid-cols-4 sm:gap-2">
            {data.map((item, index) => (
              <div
                key={`guest-title-${index}`}
                className="text-[14px]  font-medium"
              >
                {item}
              </div>
            ))}
          </div>
          <div className="gap-y-4">
            {Array(guestsNumber)
              .fill(1)
              .map((_, index) => (
                <div
                  className={`relative mt-5 ${index !== 0 ? 'border-t-2 sm:border-t-0' : 'border-t-0'}`}
                  key={`guest-${index}`}
                >
                  <div className="flex justify-between mt-[16px] mb-3 sm:hidden sm:mb-0">
                    <span className="text-primary font-normal text-[12px]">
                      Guest-{index + 1}
                    </span>
                    <X
                      size={16}
                      color="#5F6563"
                      onClick={() => handleRemove('guest', index)}
                    />
                  </div>
                  <GuestForm 
                    index={index} 
                    isDuplicate={isNameDuplicate} 
                    errors={guestErrors[index] || {}}
                    setErrors={setGuestErrorsForIndex}
                    validateField={validateField}
                  />
                  <X
                    className={`sm:absolute sm:-right-7 sm:top-2 sm:cursor-pointer ${
                      index === 0 ? 'hidden' : 'hidden sm:block'
                    }`}
                    color="#846CEC"
                    size={20}
                    onClick={() => handleRemove('guest', index)}
                  />
                </div>
              ))}
          </div>
        </div>
      </div>
      <div className="flex sm:hidden bg-background w-screen rounded-t-md p-[10px] fixed z-[100] bottom-0 right-[1px]">
        <GenerateDialogues hostErrors={hostErrors} guestErrors={guestErrors} />
      </div>
    </div>
  );
};

export default PodCastFormAssignVoices;
